import { memo } from "react";
import { TextField } from "ui/components/TextField/TextField";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { MarketplaceVehicleSpecs } from "../../../types";
import { ChipToggle, SectionCard } from "../primitives";
import {
  BODY_OPTIONS,
  CONDITION_OPTIONS,
  DRIVE_OPTIONS,
  FUEL_OPTIONS,
  SUSPENSION_OPTIONS,
  TRANSMISSION_OPTIONS,
} from "services/api/sales/listings/consts";

interface Props {
  values: MarketplaceVehicleSpecs;
  onChange: (patch: Partial<MarketplaceVehicleSpecs>) => void;
}

const VehicleSpecsSection: React.FC<Props> = ({ values, onChange }) => {
  const handleText = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({
      [e.target.name]: e.target.value,
    } as Partial<MarketplaceVehicleSpecs>);

  const handleDropdown = (
    _e: any,
    name: string,
    item: { value: string }
  ) =>
    onChange({
      [name]: item.value,
    } as Partial<MarketplaceVehicleSpecs>);

  const handleMetallicToggle = () =>
    onChange({
      metallic: !values.metallic,
    } as Partial<MarketplaceVehicleSpecs>);

  return (
    <SectionCard
      title="Tehničke specifikacije"
      subtitle="Inicijalno popunjeno iz vozila. Po potrebi prilagodite vrijednosti za ovaj oglas."
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <TextField
            type="text"
            name="manufacturingYear"
            label="Godina proizvodnje"
            value={values.manufacturingYear}
            onChange={handleText}
          />
        </div>

        <div>
          <Dropdown
            name="fuelTypeCode"
            label="Motor (gorivo)"
            value={values.fuelTypeCode}
            options={FUEL_OPTIONS}
            onChange={handleDropdown}
          />
        </div>

        <div>
          <TextField
            type="number"
            name="powerKw"
            label="Snaga motora (kW)"
            value={values.powerKw}
            onChange={handleText}
            groupAppend="kW"
          />
        </div>

        <div>
          <Dropdown
            name="driveTypeCode"
            label="Vrsta pogona"
            value={values.driveTypeCode}
            options={DRIVE_OPTIONS}
            onChange={handleDropdown}
          />
        </div>

        <div>
          <Dropdown
            name="transmissionCode"
            label="Vrsta mjenjača"
            value={values.transmissionCode}
            options={TRANSMISSION_OPTIONS}
            onChange={handleDropdown}
          />
        </div>

        <div>
          <Dropdown
            name="bodyShapeCode"
            label="Oblik karoserije"
            value={values.bodyShapeCode}
            options={BODY_OPTIONS}
            onChange={handleDropdown}
          />
        </div>

        <div>
          <TextField
            type="number"
            name="displacementCm3"
            label="Radni obujam (cm³)"
            value={values.displacementCm3}
            onChange={handleText}
            groupAppend="cm³"
          />
        </div>

        <div>
          <TextField
            type="text"
            name="modelYear"
            label="Godina modela"
            value={values.modelYear}
            onChange={handleText}
          />
        </div>

        <div>
          <TextField
            type="text"
            name="doors"
            label="Broj vrata"
            value={values.doors}
            onChange={handleText}
          />
        </div>

        <div>
          <TextField
            type="text"
            name="seats"
            label="Broj sjedala"
            value={values.seats}
            onChange={handleText}
          />
        </div>

        <div>
          <Dropdown
            name="suspensionCode"
            label="Ovjes"
            value={values.suspensionCode}
            options={SUSPENSION_OPTIONS}
            onChange={handleDropdown}
          />
        </div>

        <div>
          <Dropdown
            name="vehicleConditionCode"
            label="Stanje vozila"
            value={values.vehicleConditionCode}
            options={CONDITION_OPTIONS}
            onChange={handleDropdown}
          />
        </div>

        <div>
          <TextField
            type="text"
            name="colorId"
            label="Boja"
            value={values.colorId}
            onChange={handleText}
          />
        </div>

        <div>
          <TextField
            type="number"
            name="kmTraveled"
            label="Prijeđeni kilometri"
            value={values.kmTraveled}
            onChange={handleText}
          />
        </div>

        <div>
          <TextField
            type="text"
            name="typeName"
            label="Tip"
            value={values.typeName}
            onChange={handleText}
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Gume (širina / visina / promjer)
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <TextField
              type="text"
              name="tireWidth"
              label="Širina"
              value={values.tireWidth}
              onChange={handleText}
            />
          </div>

          <div>
            <TextField
              type="text"
              name="tireHeight"
              label="Visina"
              value={values.tireHeight}
              onChange={handleText}
            />
          </div>

          <div>
            <TextField
              type="text"
              name="tireDiameter"
              label="Promjer"
              value={values.tireDiameter}
              onChange={handleText}
            />
          </div>
        </div>
      </div>

      <div className="mt-4">
        <ChipToggle
          label="Metalik boja"
          selected={values.metallic}
          onToggle={handleMetallicToggle}
        />
      </div>
    </SectionCard>
  );
};

export default memo(VehicleSpecsSection);